<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view />
    </component>

    <incoming-call-modal
      @call:accept="callStore.acceptCall"
      @call:end="callStore.endCall"
    />
    <minimized-call-modal @call:end="callStore.endCall" />
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";

import useCallStore from "@/store-pinia/useCallStore";

import IncomingCallModal from "./components/call/IncomingCallModal";
import MinimizedCallModal from "./components/call/MinimizedCallModal";
import useChatPartnerStore from "./store-pinia/useChatPartnerStore";
import useAppStore from "./store-pinia/useAppStore";
import chatPartnerSidebar from "@/navigation/vertical/menu/chatPartner";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    IncomingCallModal,
    MinimizedCallModal,
  },

  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup() {
    const appStore = useAppStore();
    const callStore = useCallStore();
    const chatPartnerStore = useChatPartnerStore();
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    watch(
      () => appStore.profile,
      (val) => {
        try {
          if (val && (!chatPartnerStore.isConnectedChatMeRoom || !chatPartnerStore.isConnectedChatMeMessage)) {
            chatPartnerStore.getTokenAndDomain();
          }
        } catch (error) {
          console.log("==CHAT_TOKEN==", error);
        }
      },
      { immediate: true }
    );

    watch(
      () => chatPartnerStore.totalCountRoom,
      (val) => {
        try {
          chatPartnerSidebar[0].isHasMessage = val;
        } catch (error) {
          console.log("==CHAT_NOTIFY==", error);
        }
      },
      { immediate: false }
    );

    watch(
      () => chatPartnerStore.countRoom,
      (newVal) => {
        const totalCountRoom = newVal.request + newVal.joinByMe;
        chatPartnerStore.setTotalCountRoom(totalCountRoom);
      },
      { deep: true }
    );

    return {
      skinClasses,
      callStore,
      chatPartnerStore,
    };
  },
  async mounted() {
    window.addEventListener("beforeunload", this.confirmExit);

    try {
      await this.callStore.initCallClient();
    } catch (error) {
      console.error(error);
    }
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.confirmExit);
  },
  methods: {
    confirmExit(e) {
      e.preventDefault();
      e.returnValue = "";
    },
  },
  data() {
    return {
      chatPartnerSidebar,
    };
  },
};
</script>

<style>
button:disabled,
button[disabled] {
  border-color: grey !important;
}

.spinner-border.spinner-border {
  width: 1.125rem !important;
  height: 1.125rem !important;
  border-width: 2px !important;
  color: #6e6b7b !important; /* gray */
}

.mx-time-column .mx-time-item.active {
  color: #8d5185 !important;
}

.mx-btn:hover{
  border-color: #8d5185 !important;
}

.mx-time-column .mx-time-item:hover {
  background-color: rgba(141, 81, 133, 0.1) !important;
}
</style>
